import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { MessagesEvent } from "middlewares/messages";

export enum MessagesContactType {
  Practice = "PRACTICE",
  PracticeStaff = "PRACTICE_STAFF",
  Customer = "CUSTOMER",
}

export interface MessagesMessage {
  id: string;
  sender: MessagesContact;
  message: string;
  time: number;
  created_time?: number;
  state?: "DELETED" | "EDITED";
}

export interface MessagesPet {
  id: string;
  name: string;
}

export interface MessagesConversation {
  id: string;
  numUnread: number;
  lastUpdated: number;
  messages: MessagesMessage[];
  sourceId?: string;
  summary: {
    title: string;
    pet?: MessagesPet[];
    totalMessages: number;
  };
}

export interface MessagesContact {
  id: string;
  displayName: string;
  type: MessagesContactType;
}

export interface SendMessagePayload {
  conversationId: string;
  message: string;
}

export interface EditMessagePayload {
  conversationId: string;
  messageId: string;
  message: string;
}

export interface DeleteMessagePayload {
  conversationId: string;
  messageId: string;
}

export interface RestoreMessagePayload {
  conversationId: string;
  messageId: string;
}

export interface CreateConversationPayload {
  to: string;
  title: string;
  pet?: MessagesPet[];
  message: string;
  sourceId: string;
}

export interface GetConversationPayload {
  conversationId: string;
}

export interface MarkConversationAsReadPayload {
  conversationId: string;
}

export interface ReceiveMessage {
  conversationId: string;
  message: MessagesMessage;
}

interface MessagesError {
  source: MessagesEvent;
  message?: string | undefined;
}

export interface MessagesState {
  conversations: { [key: string]: MessagesConversation };
  draftConversation: Partial<MessagesConversation> | null;
  contacts: MessagesContact[];
  isConnecting: boolean;
  isConnected: boolean;
  isConnectionFailed: boolean;
  isConnectionDisconnected: boolean;
  isLoadingConversations: boolean;
  isErrorConversations: boolean;
  isLoadingConversation: boolean;
  isErrorConversation: boolean;
  error?: MessagesError | undefined;
}

const initialState: MessagesState = {
  conversations: {},
  draftConversation: null,
  contacts: [],
  isConnecting: false,
  isConnected: false,
  isConnectionFailed: false,
  isConnectionDisconnected: false,
  isLoadingConversations: true,
  isErrorConversations: false,
  isLoadingConversation: false,
  isErrorConversation: false,
  error: undefined,
};

const resetErrors = (state: MessagesState): void => {
  state.isConnectionFailed = false;
  state.isConnectionDisconnected = false;
  state.isErrorConversations = false;
  state.isErrorConversation = false;
  state.error = undefined;
};

const messagesSlice = createSlice({
  name: "messages",
  initialState,
  reducers: {
    connectionConnect: (state) => {
      state.isConnecting = true;
    },
    connectionEstablished: (state) => {
      state.isConnected = true;
      state.isConnecting = false;
      resetErrors(state);
    },

    connectionFailed: (state) => {
      state.isConnected = false;
      state.isConnecting = false;
      state.isConnectionFailed = true;
    },

    connectionDisconnected: (state) => {
      state.isConnected = false;
      state.isConnecting = false;
      state.isConnectionDisconnected = true;
    },

    getAllConversations: (state) => {
      state.isErrorConversations = false;
      state.isLoadingConversations = true;
      // Middleware intercepts to fetch from websocket
    },

    receiveAllConversations: (
      state,
      action: PayloadAction<{
        conversations: MessagesConversation[];
      }>,
    ) => {
      state.isErrorConversations = false;
      state.isLoadingConversations = false;

      const newConversationsObject = action.payload.conversations.reduce(
        (newConversations, conversation) => {
          if (state.conversations[conversation.id])
            return {
              ...newConversations,
              [conversation.id]: state.conversations[conversation.id],
            };
          return { ...newConversations, [conversation.id]: conversation };
        },
        {},
      );

      state.conversations = newConversationsObject;
    },

    createDraftConversation: (state) => {
      state.draftConversation = {
        id: "new",
        lastUpdated: Date.now(),
        numUnread: 0,
        sourceId: Date.now().toString(),
        summary: {
          title: "New Conversation",
          totalMessages: 0,
        },
      };
    },

    updateDraftConversation: (
      state,
      action: PayloadAction<{
        conversation: Partial<MessagesConversation>;
      }>,
    ) => {
      if (state.draftConversation) {
        state.draftConversation = {
          ...state.draftConversation,
          ...action.payload.conversation,
        };
      } else {
        console.error(
          "Attempting to update details on draft conversation that does not exist in store",
          action.payload.conversation,
        );
      }
    },

    resetDraftConversation: (state) => {
      state.draftConversation = null;
    },

    createConversation: (
      state,
      action: PayloadAction<CreateConversationPayload>,
    ) => {
      return;
    },

    getConversation: (state, action: PayloadAction<GetConversationPayload>) => {
      state.isErrorConversation = false;
      state.isLoadingConversation = true;
      // Middleware intercepts to fetch from websocket
    },

    receiveConversation: (
      state,
      action: PayloadAction<{
        conversation: MessagesConversation;
      }>,
    ) => {
      const conversationId = action.payload.conversation.id;
      state.conversations[conversationId] = action.payload.conversation;
      state.isErrorConversation = false;
      state.isLoadingConversation = false;
    },

    receiveConversationUpdate: (
      state,
      action: PayloadAction<{
        conversation: MessagesConversation;
      }>,
    ) => {
      const conversationId = action.payload.conversation.id;
      if (state.conversations[conversationId]) {
        state.conversations[conversationId] = {
          ...state.conversations[conversationId],
          ...action.payload.conversation,
        };
      } else {
        console.error(
          "Attempting to update details on conversation that does not exist in store",
          conversationId,
          action.payload.conversation,
        );
      }
    },

    receiveMessageUpdate: (
      state,
      action: PayloadAction<{
        conversationId: string;
        message: Partial<MessagesMessage>;
      }>,
    ) => {
      const conversationId = action.payload.conversationId;
      if (state.conversations[conversationId]) {
        const toUpdate = state.conversations[conversationId]?.messages?.find(
          (m) => m.id === action.payload.message.id,
        );
        if (toUpdate) {
          console.log("update", toUpdate, action);
          Object.assign(toUpdate, action.payload.message);
          state.conversations[conversationId] = {
            ...state.conversations[conversationId],
            messages: state.conversations[conversationId].messages.slice(),
          };
        }
      }
    },

    markConversationAsRead: (
      state,
      action: PayloadAction<MarkConversationAsReadPayload>,
    ) => {
      state.conversations[action.payload.conversationId].numUnread = 0;
    },

    sendMessage: (state, action: PayloadAction<SendMessagePayload>) => {
      // Middleware intercepts to send from websocket
    },

    editMessage: (state, action: PayloadAction<EditMessagePayload>) => {
      // Middleware intercepts to send from websocket
    },

    deleteMessage: (state, action: PayloadAction<DeleteMessagePayload>) => {
      // Middleware intercepts to send from websocket
    },

    restoreMessage: (state, action: PayloadAction<RestoreMessagePayload>) => {
      // Middleware intercepts to send from websocket
    },

    receiveMessage: (
      state,
      action: PayloadAction<{
        conversationId: string;
        message: MessagesMessage;
      }>,
    ) => {
      const conversation = state.conversations[action.payload.conversationId];

      if (conversation) {
        conversation.numUnread++;
        conversation.summary.totalMessages++;
        conversation.lastUpdated = action.payload.message.time;
        if (conversation.messages) {
          conversation.messages.push(action.payload.message);
        }
      }

      state.conversations[action.payload.conversationId] = conversation;
    },

    setError: (state, action: PayloadAction<MessagesError>) => {
      if (action.payload.source === MessagesEvent.GetAllConversations) {
        state.isErrorConversations = true;
        state.isLoadingConversations = false;
      }
      if (action.payload.source === MessagesEvent.GetConversation) {
        state.isErrorConversation = true;
        state.isLoadingConversation = false;
      }
      if (action.payload.message) {
        state.error = action.payload;
      }
    },

    clearError: (state) => {
      state.error = undefined;
    },
  },
});

export const {
  connectionConnect,
  connectionEstablished,
  connectionFailed,
  connectionDisconnected,
  getAllConversations,
  receiveAllConversations,
  createDraftConversation,
  updateDraftConversation,
  resetDraftConversation,
  createConversation,
  getConversation,
  receiveConversation,
  receiveConversationUpdate,
  receiveMessageUpdate,
  markConversationAsRead,
  sendMessage,
  editMessage,
  deleteMessage,
  restoreMessage,
  receiveMessage,
  setError,
  clearError,
} = messagesSlice.actions;

export default messagesSlice.reducer;
